import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { componentScope } from '../lib/cssScope'
import { Button } from './ui/Button'
import shellStyles from './tpsm/SupplierFormModal.module.css'
import localStyles from './ScanDetailsModal.module.css'

interface ScanDetailsModalProps {
  open: boolean
  onClose: () => void
  data?: {
    taskId: string
    name: string
    config: string
    target: string
    scanner: string
    status: string
    progress: number | string
    reportId?: string | null
    startedAt?: string | null
    finishedAt?: string | null
    resultCount?: number | null
  }
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    <div className="w-40 shrink-0 text-[var(--ds-text-secondary,#64748b)]">{label}</div>
    <div className="min-w-0 flex-1 break-all font-medium text-[var(--ds-text,#0f172a)]">{value ?? '—'}</div>
  </div>
)

/** Ignore stray click / pointerup on backdrop right after open (same gesture as "View Details"). */
const BACKDROP_CLOSE_GUARD_MS = 320

export default function ScanDetailsModal({ open, onClose, data }: ScanDetailsModalProps) {
  const suppressBackdropCloseRef = useRef(false)

  useEffect(() => {
    if (!open) return
    suppressBackdropCloseRef.current = true
    const t = window.setTimeout(() => {
      suppressBackdropCloseRef.current = false
    }, BACKDROP_CLOSE_GUARD_MS)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) {
    return null
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return
    if (suppressBackdropCloseRef.current) return
    onClose()
  }

  const node = (
    <div
      className={`${componentScope('ScanDetailsModal')} ${shellStyles.backdrop} ${localStyles.portalBackdrop}`}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className={`${shellStyles.dialog} max-w-xl`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-details-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={shellStyles.header}>
          <h2 id="scan-details-modal-title" className={shellStyles.title}>
            Scan Details
          </h2>
          <Button
            type="button"
            color="outline"
            iconOnly
            onClick={onClose}
            className={shellStyles.closeBtn}
            aria-label="Close"
            icon={<X className="h-5 w-5" aria-hidden />}
          >
            <span className="sr-only">Close</span>
          </Button>
        </div>
        <div className={`${shellStyles.form} gap-3`}>
          <Row label="Task ID" value={data?.taskId} />
          <Row label="Name" value={data?.name} />
          <Row label="Configuration" value={data?.config} />
          <Row label="Target" value={data?.target} />
          <Row label="Scanner" value={data?.scanner} />
          <Row label="Status" value={(data?.status || '').toString()} />
          <Row label="Progress" value={`${data?.progress ?? 0}%`} />
          <Row label="Report ID" value={data?.reportId || '—'} />
          <Row label="Started" value={data?.startedAt || '—'} />
          <Row label="Results" value={data?.resultCount ?? '—'} />
        </div>
        <div className={shellStyles.footer}>
          <Button type="button" color="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined' || !document.body) return null
  return createPortal(node, document.body)
}
